import type { AnalyticsNavigationSource, AnalyticsNavigationSurface } from '@/lib/analyticsContext';

export type AnalyticsNavigationIntent = {
  source: AnalyticsNavigationSource;
  surface: AnalyticsNavigationSurface;
  intent: string;
  to: string;
};

const pendingNavigationStorageKey = 'supplyspark:analytics:pending-navigation';

let pendingNavigation: AnalyticsNavigationIntent | null = null;

function isBrowser() {
  return typeof window !== 'undefined';
}

function normalizeTargetPath(value: string) {
  try {
    const url = new URL(value, isBrowser() ? window.location.origin : 'http://localhost');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

export function recordAnalyticsNavigation(intent: AnalyticsNavigationIntent) {
  const normalizedIntent = {
    ...intent,
    to: normalizeTargetPath(intent.to),
  };

  pendingNavigation = normalizedIntent;

  if (!isBrowser()) {
    return;
  }

  window.sessionStorage.setItem(pendingNavigationStorageKey, JSON.stringify(normalizedIntent));
}

export function clearAnalyticsNavigation() {
  pendingNavigation = null;
  if (isBrowser()) {
    window.sessionStorage.removeItem(pendingNavigationStorageKey);
  }
}

export function consumeAnalyticsNavigation(currentPath: string) {
  const normalizedCurrentPath = normalizeTargetPath(currentPath);
  const inMemoryIntent = pendingNavigation;

  if (inMemoryIntent && normalizeTargetPath(inMemoryIntent.to) === normalizedCurrentPath) {
    pendingNavigation = null;
    if (isBrowser()) {
      window.sessionStorage.removeItem(pendingNavigationStorageKey);
    }
    return inMemoryIntent;
  }

  if (!isBrowser()) {
    return null;
  }

  const storedValue = window.sessionStorage.getItem(pendingNavigationStorageKey);
  if (!storedValue) {
    return null;
  }

  try {
    const storedIntent = JSON.parse(storedValue) as AnalyticsNavigationIntent;
    if (normalizeTargetPath(storedIntent.to) !== normalizedCurrentPath) {
      return null;
    }
    window.sessionStorage.removeItem(pendingNavigationStorageKey);
    pendingNavigation = null;
    return storedIntent;
  } catch {
    window.sessionStorage.removeItem(pendingNavigationStorageKey);
    pendingNavigation = null;
    return null;
  }
}

export function resetAnalyticsNavigationForTests() {
  clearAnalyticsNavigation();
}
